import React, { useState, useRef, useEffect } from 'react';
import './user-avatar.scss';

interface UserAvatarProps {
  /** User's email for display in dropdown */
  email: string;
  /** User's first name */
  firstName?: string;
  /** User's last name */
  lastName?: string;
  /** Sign out function */
  onSignOut: () => void;
  /** Whether sign out is in progress */
  isSigningOut?: boolean;
  /** Opens the change-password modal */
  onChangePassword?: () => void;
}

const UserAvatar: React.FC<UserAvatarProps> = ({
  email,
  firstName,
  lastName,
  onSignOut,
  isSigningOut = false,
  onChangePassword
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get initials from first name and last name, fallback to email extraction
  const getInitials = (): string => {
    // If we have both first and last name, use those
    if (firstName && lastName) {
      return (firstName[0] + lastName[0]).toUpperCase();
    }
    
    // If we only have first name, use first letter twice or first + second letter
    if (firstName) {
      if (firstName.length >= 2) {
        return (firstName[0] + firstName[1]).toUpperCase();
      } else {
        return (firstName[0] + firstName[0]).toUpperCase();
      }
    }
    
    // If we only have last name, use first letter twice or first + second letter
    if (lastName) {
      if (lastName.length >= 2) {
        return (lastName[0] + lastName[1]).toUpperCase();
      } else {
        return (lastName[0] + lastName[0]).toUpperCase();
      }
    }
    
    // Fallback to email extraction (simplified version)
    if (!email) return 'U';
    
    const emailPrefix = email.split('@')[0];
    const parts = emailPrefix.split(/[._-]/).filter(part => part.length > 0);
    
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    } else if (parts[0] && parts[0].length >= 2) {
      return (parts[0][0] + parts[0][1]).toUpperCase();
    } else {
      return parts[0] ? (parts[0][0] + 'U').toUpperCase() : 'UU';
    }
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isDropdownOpen]);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleSignOut = () => {
    setIsDropdownOpen(false);
    onSignOut();
  };

  const initials = getInitials();

  return (
    <div className="user-avatar-container" ref={dropdownRef}>
      <button
        className="user-avatar"
        onClick={toggleDropdown}
        aria-label="User menu"
        aria-expanded={isDropdownOpen}
        aria-haspopup="true"
      >
        <span className="user-initials">{initials}</span>
      </button>

      {isDropdownOpen && (
        <div className="user-dropdown">
          <div className="user-dropdown-header">
            <div className="user-email">{email}</div>
          </div>
          <div className="user-dropdown-divider"></div>
          <div className="user-dropdown-actions">
            {onChangePassword && (
              <button
                className="dropdown-action"
                onClick={() => {
                  setIsDropdownOpen(false);
                  onChangePassword();
                }}
              >
                Change Password
              </button>
            )}
            <button
              className="dropdown-action sign-out-action"
              onClick={handleSignOut}
              disabled={isSigningOut}
            >
              {isSigningOut ? 'Signing out...' : 'Sign Out'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAvatar;
